const { date } = require('./utils')
const { ONE_HOUR } = require('./timeConstants')

const AVG_MONTH_DURATION_MILLIS = 1000 * 60 * 60 * 24 * 365.2425 / 12

const DEFAULT_RELATIVE_THRESHOLD = ONE_HOUR * 12

function shortTimeString(realDate, includeNumericTime) {
  return realDate.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: includeNumericTime ? 'short' : undefined,
  }).replace(/:00/g, '').replace(/ PM/g, 'pm').replace(/ AM/g, 'am')
}

function longTimeString(realDate, includeNumericTime) {
  return realDate.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: includeNumericTime ? 'short' : undefined,
  })
}

function relativeMillisString(positiveOrNegativeMillis, { hoursThreshold = 24, daysThreshold = 7, weeksThreshold = 6 } = {}) {
  const millis = Math.abs(positiveOrNegativeMillis)

  const seconds = millis / 1000

  if (seconds < 60) return 'Moments'

  const minutes = Math.floor(seconds / 60)

  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'}`

  const hours = Math.floor(minutes / 60)

  if (hours <= hoursThreshold) return `${hours} hr${hours === 1 ? '' : 's'}`

  const days = Math.floor(hours / 24)

  if (days <= daysThreshold) return `${days} day${days === 1 ? '' : 's'}`

  const weeks = Math.floor(days / 7)

  if (weeks <= weeksThreshold) return `${weeks} week${weeks === 1 ? '' : 's'}`

  const months = Math.floor(millis / AVG_MONTH_DURATION_MILLIS)

  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`

  const years = Math.floor(months / 12)

  return `${years} year${years === 1 ? '' : 's'}`
}

function shortenedRelativeMillisString(positiveOrNegativeMillis, { hoursThreshold = 24, daysThreshold = 7, weeksThreshold = 6 } = {}) {
  const millis = Math.abs(positiveOrNegativeMillis)

  const seconds = millis / 1000

  if (seconds < 60) return 'Moments'

  const minutes = Math.floor(seconds / 60)

  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)

  if (hours <= hoursThreshold) return `${hours}h`

  const days = Math.floor(hours / 24)

  if (days <= daysThreshold) return `${days}d`

  const weeks = Math.floor(days / 7)

  if (weeks <= weeksThreshold) return `${weeks}w`

  const months = Math.floor(millis / AVG_MONTH_DURATION_MILLIS)

  if (months < 12) return `${months}mo`

  const years = Math.floor(months / 12)

  return `${years}y`
}

function relativeMillisTimerString(positiveOrNegativeMillis) {
  const millis = Math.abs(positiveOrNegativeMillis)
  return new Date(millis).toISOString().substring(11, 19)
}

function fromNowString(dateOrTimestamp, { includeNumericTime, singleLetters, relativeThreshold = DEFAULT_RELATIVE_THRESHOLD, ...options } = {}) {
  const realDate = date(dateOrTimestamp)

  const millis = realDate.getTime() - new Date().getTime()

  if (relativeThreshold < 0 || millis <= relativeThreshold) {
    const relativeString = `${(singleLetters ? shortenedRelativeMillisString : relativeMillisString)(millis, options)} from now`
    if (includeNumericTime) return `${fromNowString(dateOrTimestamp)} (${relativeString})`
    return relativeString
  }

  const offsetMins = new Date().getTimezoneOffset()

  const postDay = Math.floor((realDate.getTime() / 1000 / 60 - offsetMins) / 60 / 24)
  const nowDay = Math.floor((new Date().getTime() / 1000 / 60 - offsetMins) / 60 / 24)

  const timeString = shortTimeString(realDate, includeNumericTime)

  if (postDay === nowDay) return `Today at ${timeString}`

  if (postDay === nowDay - 1) return `Tomorrow at ${timeString}`

  return longTimeString(realDate, includeNumericTime)
}

function inString(dateOrTimestamp, { includeNumericTime, singleLetters, relativeThreshold = DEFAULT_RELATIVE_THRESHOLD, ...options } = {}) {
  const realDate = date(dateOrTimestamp)

  const millis = realDate.getTime() - new Date().getTime()

  if (relativeThreshold < 0 || millis <= relativeThreshold) {
    const seconds = millis / 1000

    const relativeString = seconds < 60 ? 'Momentarily' : `in ${(singleLetters ? shortenedRelativeMillisString : relativeMillisString)(millis, options)}`
    if (includeNumericTime) return `${inString(dateOrTimestamp)} (${relativeString})`
    return relativeString
  }

  const offsetMins = new Date().getTimezoneOffset()

  const postDay = Math.floor((realDate.getTime() / 1000 / 60 - offsetMins) / 60 / 24)
  const nowDay = Math.floor((new Date().getTime() / 1000 / 60 - offsetMins) / 60 / 24)

  const timeString = shortTimeString(realDate, includeNumericTime)

  if (postDay === nowDay) return `Today at ${timeString}`

  if (postDay === nowDay - 1) return `Tomorrow at ${timeString}`

  return longTimeString(realDate, includeNumericTime)
}

function agoString(dateOrTimestamp, { showAsTimer, includeNumericTime, singleLetters, relativeThreshold = DEFAULT_RELATIVE_THRESHOLD, ...options } = {}) {
  const realDate = date(dateOrTimestamp)

  const millis = new Date().getTime() - realDate.getTime()

  if (showAsTimer) {
    return relativeMillisTimerString(Math.max(0, millis))
  }

  if (relativeThreshold < 0 || millis <= relativeThreshold) {
    const relativeString = `${(singleLetters ? shortenedRelativeMillisString : relativeMillisString)(millis, options)} ago`
    if (includeNumericTime) return `${agoString(dateOrTimestamp)} (${relativeString})`
    return relativeString
  }

  const offsetMins = new Date().getTimezoneOffset()

  const postDay = Math.floor((realDate.getTime() / 1000 / 60 - offsetMins) / 60 / 24)
  const nowDay = Math.floor((new Date().getTime() / 1000 / 60 - offsetMins) / 60 / 24)

  const timeString = shortTimeString(realDate, includeNumericTime)

  if (postDay === nowDay) return `Today at ${timeString}`

  if (postDay === nowDay - 1) return `Yesterday at ${timeString}`

  return longTimeString(realDate, includeNumericTime)
}

// relativeThreshold: time in millis to stop showing relative time and only show long time
function relativeTimeString(dateOrTimestamp, { preferShort, ...options } = {}) {
  const realDate = date(dateOrTimestamp)

  if (new Date().getTime() - realDate.getTime() > 0) {
    return agoString(realDate, options)
  }
  return (preferShort ? inString : fromNowString)(realDate, options)
}

Object.assign(exports, {
  relativeMillisTimerString,
  fromNowString,
  inString,
  agoString,
  relativeTimeString,
})
